import { FC, useEffect, useRef, useState } from 'react';
import { AnchorProps, Direction } from 'src/components/tag-tooltip/Components/Anchor/types';
import { determineTooltipSide } from 'src/components/tag-tooltip/Components/Anchor/utils';
import { Triangle, Wrapper } from 'src/components/tag-tooltip/Components/Anchor/styles';
import { DeviceManager } from 'src/utils';
import * as THREE from 'three';
import { useWindowSize } from 'usehooks-ts';

const defaultSettings = {
  offsetFromTag: 25,
  width: 380
};

export const Anchor: FC<AnchorProps & { children?: any }> = ({ tag, camera, renderer, settings = {}, children }) => {
  const tooltipSettings = { ...defaultSettings, ...settings };
  const { width: screenWidth } = useWindowSize();
  const tooltipRef = useRef<any>();
  const tagRef = useRef<any>();
  const triangleRef = useRef<any>();
  const animationFrame = useRef<any>();
  const [triangleDirection, setTriangleDirection] = useState<Direction>(Direction.Left);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const animate = () => {
    animationFrame.current = requestAnimationFrame(animate);
    if (!tagRef.current || !tooltipRef.current || !tagRef.current.dummyObject) return;
    const vector = new THREE.Vector3();
    vector.setFromMatrixPosition(tagRef.current.dummyObject.matrixWorld);
    // @ts-ignore
    const pixelRatio = renderer.getPixelRatio();
    vector.project(camera);

    // Check if the object is behind the camera
    const cameraDirection = new THREE.Vector3();
    const cameraPosition = new THREE.Vector3();
    const dummyObjectPosition = new THREE.Vector3();
    tagRef.current.dummyObject.getWorldPosition(dummyObjectPosition);

    camera.getWorldDirection(cameraDirection);
    camera.getWorldPosition(cameraPosition);
    const toObject = new THREE.Vector3();
    toObject.subVectors(dummyObjectPosition, cameraPosition);
    const dotProduct = cameraDirection.dot(toObject.normalize());

    if (dotProduct <= 0) {
      setTooltipVisible(false);
      return;
    }

    vector.x = Math.round((0.5 + vector.x / 2) * (renderer.domElement.width / pixelRatio));
    vector.y = Math.round((0.5 - vector.y / 2) * (renderer.domElement.height / pixelRatio));

    const tooltipSide = DeviceManager.isMobile()
      ? Direction.Top
      : determineTooltipSide(
          renderer.domElement,
          tooltipRef.current.offsetWidth,
          tooltipRef.current.offsetHeight,
          vector.x,
          vector.y, // TODO add tag offset support to this function
          pixelRatio
        );

    switch (tooltipSide) {
      case 'top': {
        tooltipRef.current.style.transform = `translate(${
          vector.x - tooltipRef.current.offsetWidth / 2
        }px, ${vector.y - tooltipRef.current.offsetHeight - tooltipSettings.offsetFromTag}px)`;
        setTriangleDirection(Direction.Bottom);
        break;
      }
      case 'bottom': {
        tooltipRef.current.style.transform = `translate(${
          vector.x - tooltipRef.current.offsetWidth / 2
        }px, ${vector.y + tooltipSettings.offsetFromTag}px)`;
        setTriangleDirection(Direction.Top);
        break;
      }
      case 'left': {
        tooltipRef.current.style.transform = `translate(${
          vector.x - tooltipRef.current.offsetWidth - +tooltipSettings.offsetFromTag
        }px, ${vector.y - tooltipRef.current.offsetHeight / 2}px)`;
        setTriangleDirection(Direction.Right);
        break;
      }
      case 'right': {
        tooltipRef.current.style.transform = `translate(${
          vector.x + tooltipSettings.offsetFromTag
        }px, ${vector.y - tooltipRef.current.offsetHeight / 2}px)`;
        setTriangleDirection(Direction.Left);
        break;
      }
      default:
        break;
    }

    if (tooltipSide === 'no space') setTooltipVisible(false);
    else {
      setTooltipVisible(true);
    }
  };

  useEffect(() => {
    animate();
    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  useEffect(() => {
    tagRef.current = tag;
  }, [tag, camera]);

  if (!tag) return null;
  return (
    <Wrapper
      width={screenWidth >= 380 ? tooltipSettings.width : undefined}
      height="auto"
      visible={tooltipVisible}
      ref={tooltipRef}
    >
      {/*@ts-ignore*/}
      <Triangle ref={triangleRef} direction={triangleDirection} />
      {children}
    </Wrapper>
  );
};
